import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle, CalendarClock, ChevronRight, ClipboardList, HardHat, PackageCheck,
  Truck, Users, Wrench,
} from 'lucide-react';
import { KPICard, Skeleton, StatusBadge } from '@/components/ui';
import { getDashboardOperationnel } from '@/services/dashboard';
import type { StatusKey } from '@/types';

function Carte({ title, icon, children, className = '' }: { title: string; icon?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-lg border border-border bg-surface-secondary p-5 shadow-sm ${className}`}>
      <div className="mb-4 flex items-center gap-2">{icon}<h2 className="text-base font-bold text-content-primary">{title}</h2></div>
      {children}
    </div>
  );
}

const STATUT_AVCT: Record<string, StatusKey> = { EN_COURS: 'working', EN_PREPARATION: 'pending', SUSPENDU: 'stuck', ACHEVE: 'done' };
function barColor(pct: number, retard: boolean) {
  if (retard) return '#E2445C';
  if (pct >= 75) return '#00C875';
  if (pct >= 40) return '#0073EA';
  return '#F97316';
}

export function DashboardOperationnelPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ['dashboard-operationnel'], queryFn: getDashboardOperationnel });

  if (isLoading || !data) {
    return <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28" />)}</div>;
  }
  const { kpis, avancement, planning, echeances_jour, a_traiter, pointage_jour, alertes } = data;
  const nbAlertes = alertes.engins_maintenance.length + alertes.retard_planning.length + alertes.stocks_bas.length;

  return (
    <div className="space-y-6">
      {/* Indicateurs terrain */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KPICard title="Chantiers actifs" value={String(kpis.chantiers_actifs)} icon={HardHat} accent="#1a1a2e" changeLabel="en cours" />
        <KPICard title="Effectif présent" value={String(kpis.effectif_present)} icon={Users} accent="#00C875" changeLabel="pointés aujourd'hui" />
        <KPICard title="Engins en service" value={`${kpis.engins_service}/${kpis.engins_total}`} icon={Truck} accent="#64748b" changeLabel="du parc" />
        <KPICard title="Tâches en retard" value={String(kpis.taches_retard)} icon={AlertTriangle} accent={kpis.taches_retard > 0 ? '#F56565' : '#A0AEC0'} changeLabel="planning" />
      </div>

      {/* À traiter */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <ATraiter n={a_traiter.bm_attente} label="Demandes matériaux" sub="à traiter" icon={<ClipboardList className="size-5" />} color="#1a1a2e" onClick={() => navigate('/achats')} />
        <ATraiter n={a_traiter.livraisons_attendues} label="Livraisons attendues" sub="bons de commande" icon={<PackageCheck className="size-5" />} color="#64748b" onClick={() => navigate('/achats')} />
        <ATraiter n={a_traiter.situations_a_etablir} label="Situations à établir" sub="ce mois" icon={<CalendarClock className="size-5" />} color="#F97316" onClick={() => navigate('/situations')} />
        <ATraiter n={a_traiter.maintenances_planifiees} label="Maintenances" sub="à traiter" icon={<Wrench className="size-5" />} color="#94a3b8" onClick={() => navigate('/engins')} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Avancement des chantiers */}
        <Carte title="Avancement des chantiers" icon={<HardHat className="size-4 text-brand-primary" />} className="lg:col-span-2">
          {avancement.length ? (
            <ul className="space-y-3">
              {avancement.map((c) => (
                <li key={c.id} className="cursor-pointer rounded-md px-2 py-1.5 hover:bg-surface-hover" onClick={() => navigate(`/chantiers/${c.id}`)}>
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-medium text-content-primary">{c.nom}</span>
                    <span className="flex shrink-0 items-center gap-2">
                      {c.retard ? <StatusBadge status="stuck" label="En retard" /> : <StatusBadge status={STATUT_AVCT[c.statut] ?? 'working'} label={`${c.avancement.toFixed(0)}%`} />}
                      <span className="text-xs tabular-nums text-content-muted">{c.jours_restants === null ? '—' : c.jours_restants < 0 ? `${-c.jours_restants} j de retard` : `fin dans ${c.jours_restants} j`}</span>
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-surface-primary">
                    <div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.max(2, c.avancement))}%`, backgroundColor: barColor(c.avancement, c.retard) }} />
                  </div>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-content-muted">Aucun chantier actif.</p>}
        </Carte>

        {/* Pointage du jour */}
        <Carte title="Pointage du jour" icon={<Users className="size-4 text-brand-primary" />}>
          <div className="mb-3 flex items-end justify-between rounded-md bg-surface-primary px-3 py-2">
            <div><p className="text-2xl font-bold tabular-nums text-status-done">{pointage_jour.total_pointes}</p><p className="text-xs text-content-muted">présents</p></div>
            <div className="text-right"><p className="text-lg font-semibold tabular-nums text-content-secondary">/ {pointage_jour.total_attendus}</p><p className="text-xs text-content-muted">attendus</p></div>
          </div>
          {pointage_jour.par_chantier.length ? (
            <ul className="space-y-2">
              {pointage_jour.par_chantier.slice(0, 7).map((p, i) => {
                // « Non affecté » n'est PAS un signal positif : on le marque en ambre
                // (employés sans chantier), jamais en vert même si tous ont pointé.
                const nonAffecte = p.chantier === 'Non affecté';
                return (
                  <li key={i} className="flex items-center justify-between gap-2 text-sm">
                    <span className={`truncate ${nonAffecte ? 'font-medium text-status-working' : 'text-content-secondary'}`}>
                      {nonAffecte ? '⚠ Non affecté' : p.chantier}
                    </span>
                    <span className="shrink-0 tabular-nums">
                      <span className={`font-semibold ${nonAffecte ? 'text-status-working' : 'text-status-done'}`}>{p.pointes}</span>
                      <span className="text-content-muted">/{p.attendus}</span>
                      {p.manquants > 0 && <span className="ml-1.5 rounded bg-status-stuck/15 px-1.5 text-xs font-semibold text-status-stuck">−{p.manquants}</span>}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : <p className="text-sm text-content-muted">Aucun employé affecté.</p>}
          <button onClick={() => navigate('/rh')} className="mt-3 flex w-full items-center justify-center gap-1 rounded-md border border-border py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-hover">Ouvrir le pointage <ChevronRight className="size-3.5" /></button>
        </Carte>
      </div>

      {/* Planning de la semaine */}
      <Carte title="Planning de la semaine" icon={<CalendarClock className="size-4 text-brand-primary" />}>
        {echeances_jour.length > 0 && (
          <div className="mb-3 rounded-md border border-status-working/30 bg-status-working/5 px-3 py-2">
            <p className="text-xs font-semibold uppercase text-status-working">Échéances du jour ({echeances_jour.length})</p>
            <ul className="mt-1 space-y-0.5">{echeances_jour.map((t, i) => <li key={i} className="text-sm text-content-secondary">• {t.tache} — <span className="text-content-muted">{t.chantier}</span></li>)}</ul>
          </div>
        )}
        {planning.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-xs text-content-muted"><th className="py-2">Échéance</th><th className="py-2">Tâche</th><th className="py-2">Chantier</th><th className="py-2 text-right">Avct</th></tr></thead>
              <tbody>
                {planning.map((t, i) => (
                  <tr key={i} className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-surface-hover" onClick={() => navigate(t.lien)}>
                    <td className="py-1.5 tabular-nums"><span className={t.jours < 0 ? 'font-semibold text-status-stuck' : t.jours === 0 ? 'font-semibold text-status-working' : 'text-content-secondary'}>{t.jours < 0 ? `${-t.jours} j de retard` : t.jours === 0 ? "Aujourd'hui" : `J+${t.jours}`}</span></td>
                    <td className="py-1.5 font-medium text-content-primary">{t.tache}</td>
                    <td className="py-1.5 text-content-secondary">{t.chantier}</td>
                    <td className="py-1.5 text-right tabular-nums text-content-muted">{t.avancement.toFixed(0)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-sm text-content-muted">Aucune tâche planifiée cette semaine.</p>}
      </Carte>

      {/* Alertes opérationnelles */}
      <Carte title={`Alertes opérationnelles${nbAlertes ? ` (${nbAlertes})` : ''}`} icon={<AlertTriangle className="size-4 text-status-working" />}>
        <div className="grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-2">
          {alertes.engins_maintenance.map((a) => (
            <AlerteLigne key={`e${a.id}`} couleur="review" tag="Engin" onClick={() => navigate(a.lien)} texte={`${a.code} ${a.designation} — ${a.raison}${a.date ? ` (${new Date(a.date).toLocaleDateString('fr-FR')})` : ''}`} />
          ))}
          {alertes.retard_planning.map((a) => (
            <AlerteLigne key={`r${a.id}`} couleur="stuck" tag="Retard" onClick={() => navigate(a.lien)} texte={`${a.nom} — ${a.jours_retard} j de retard`} />
          ))}
          {alertes.stocks_bas.map((a, i) => (
            <AlerteLigne key={`s${i}`} couleur="working" tag="Stock" onClick={() => navigate(a.lien)} texte={`${a.designation} — ${a.quantite} ${a.unite} (seuil ${a.seuil})`} />
          ))}
        </div>
        {nbAlertes === 0 && <p className="text-sm text-content-muted">Aucune alerte. 🎉</p>}
      </Carte>
    </div>
  );
}

function ATraiter({ n, label, sub, icon, color, onClick }: { n: number; label: string; sub: string; icon: React.ReactNode; color: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="flex items-center gap-3 rounded-lg border border-border bg-surface-secondary p-4 text-left shadow-sm transition-shadow hover:shadow-md">
      <span className="flex size-11 shrink-0 items-center justify-center rounded-md" style={{ backgroundColor: `${color}1A`, color }}>{icon}</span>
      <span className="min-w-0">
        <span className="block text-2xl font-bold tabular-nums text-content-primary">{n}</span>
        <span className="block truncate text-xs font-medium text-content-secondary">{label}</span>
        <span className="block text-[11px] text-content-muted">{sub}</span>
      </span>
    </button>
  );
}

const TAG_BG: Record<string, string> = { stuck: 'bg-status-stuck/15 text-status-stuck', working: 'bg-status-working/15 text-status-working', review: 'bg-purple-500/15 text-purple-600', info: 'bg-brand-primary/15 text-brand-primary' };
function AlerteLigne({ couleur, tag, texte, onClick }: { couleur: string; tag: string; texte: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left hover:bg-surface-hover">
      <span className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${TAG_BG[couleur] ?? TAG_BG.info}`}>{tag}</span>
      <span className="flex-1 text-sm text-content-secondary">{texte}</span>
      <ChevronRight className="mt-0.5 size-4 shrink-0 text-content-muted" />
    </button>
  );
}
